import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";

/**
 * Modal to record that an employee has been paid for a sheet.
 * Requires a file upload (image or PDF), and pre-fills the amount with the
 * resolved pay for that employee on that sheet.
 *
 * Props:
 *   open
 *   sheetId
 *   employee       : { user_id, name, amount } row from the pay split
 *   currencySymbol
 *   onClose
 *   onSaved
 */
export default function EmployeePayoutModal({
  open,
  sheetId,
  employee,
  currencySymbol = "$",
  onClose,
  onSaved,
}) {
  const [file, setFile] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !employee) return;
    setFile(null);
    setAmount(employee.amount != null ? String(employee.amount) : "");
    setErr(null);
    setBusy(false);
  }, [open, employee]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose, busy]);

  if (!open || !employee) return null;

  const handleSave = async () => {
    if (!file) {
      setErr("Upload a proof file (image or PDF).");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const form = new FormData();
      form.append("file", file);
      if (amount !== "") form.append("amount", amount);
      await api.post(
        `/sheets/${sheetId}/employee-payouts/${employee.user_id}/mark-paid`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      onSaved();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 460 }}
        role="dialog"
        aria-modal="true"
      >
        <h3>Mark paid</h3>
        <p className="muted" style={{ marginTop: 4, marginBottom: 16 }}>
          Recording that <strong style={{ color: "var(--ink)" }}>{employee.name}</strong>{" "}
          has been paid for this milestone.
        </p>

        <div style={{ marginBottom: 14 }}>
          <label className="field">Amount</label>
          <div className="amount-input-wrap">
            <span className="amount-prefix">{currencySymbol}</span>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="field">Proof of payment (image or PDF)</label>
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          {file && (
            <div className="muted-sm" style={{ marginTop: 6 }}>
              {file.name} · {(file.size / 1024).toFixed(1)} KB
            </div>
          )}
        </div>

        {err && <div className="error" style={{ marginTop: 14 }}>{err}</div>}

        <div className="row-h" style={{ justifyContent: "flex-end", marginTop: 20, gap: 8 }}>
          <button type="button" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="primary"
            disabled={busy || !file}
            onClick={handleSave}
          >
            {busy ? "Saving…" : "Mark paid"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
